import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { ApiClient, ElektroGruppe, ElektroBauteilseite } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, Feld, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte,
} from '../muster';

/**
 * Die Elektro-Produktdaten: erst die Gruppen, dann die Bauteile.
 *
 * --- Warum zuerst die Gruppen -------------------------------------------------
 *
 * 30.218 Elektrobauteile. Die erste Fassung dieser Seite holte 5.000 davon und
 * schnitt den Rest ab — ohne es zu sagen. Eine Liste, die stillschweigend
 * endet, ist schlimmer als eine, die gar nicht kommt: Sie sieht vollständig
 * aus.
 *
 * Die Übersicht nennt je Bauteilart die Zahl und wie viele davon ein Fabrikat
 * tragen. Das ist die Frage, mit der jemand diese Seite aufmacht — wo fehlen
 * noch Produktdaten.
 *
 * --- Warum seitenweise und nicht alles ----------------------------------------
 *
 * Hundert je Seite. Bei 12.000 Steckdosen sind das 120 Seiten — aber sie sind
 * alle erreichbar, und das ist der Unterschied zu einer Liste, die bei 5.000
 * endet.
 *
 * Das Blättern läuft weiterhin über den SERVER. `DataTable` bekommt deshalb
 * `proSeite={0}` und einen eigenen `trefferText` (so vorgesehen in
 * `docs/frontend-muster.md`, Abschnitt 1) — die Tabelle zählt sonst nur die
 * hundert Zeilen, die gerade geladen sind, und behauptete „100 Treffer".
 *
 * --- Warum die Filter der eigentliche Weg sind --------------------------------
 *
 * Niemand blättert 120 Seiten. Wer ein bestimmtes Bauteil sucht, gibt seinen
 * Raum oder sein Geschoss ein — und findet es auf der ersten Seite.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

export function ElektroGeraete() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [gruppen, setGruppen] = useState<ElektroGruppe[] | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(() => {
    if (!projekt) return;
    setFehler(null);

    api.elektroGruppen(projekt.id)
      .then(setGruppen)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id]);

  useEffect(() => { laden(); }, [laden]);

  const spalten: Spalte<ElektroGruppe>[] = useMemo(() => [
    {
      schluessel: 'bezeichnung', titel: 'Bauteilart', immerSichtbar: true,
      wert: (g) => g.bezeichnung,
      inhalt: (g) => (
        <>{gewaehlt === g.bauteilart ? '▾ ' : '▸ '}{g.bezeichnung}</>
      ),
    },
    { schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl', wert: (g) => g.bauteile },
    {
      schluessel: 'fabrikat', titel: 'mit Fabrikat', art: 'status', breite: '190px',
      wert: (g) => g.mitFabrikat,
      inhalt: (g) => (g.mitFabrikat === 0
        ? <StatusBadge zustand="warnung" text="kein Fabrikat" klein />
        : g.mitFabrikat >= g.bauteile
          ? <StatusBadge zustand="gut" text="vollständig" klein />
          : (
            <StatusBadge
              zustand="warnung"
              text={`${g.mitFabrikat.toLocaleString('de-DE')} von ${g.bauteile.toLocaleString('de-DE')}`}
              klein
            />
          )),
    },
    { schluessel: 'raeume', titel: 'Räume', art: 'zahl', wert: (g) => g.raeume },
    { schluessel: 'geschosse', titel: 'Geschosse', art: 'zahl', wert: (g) => g.geschosse },
  ], [gewaehlt]);

  const kopf = (
    <PageHeader
      titel="Elektro — Produktdaten"
      beschreibung={'Je Bauteilart die Zahl der Bauteile und wie viele davon ein Fabrikat '
        + 'tragen. Eine Bauteilart anklicken öffnet ihre Bauteile — hundert je Seite, '
        + 'mit Filter nach Raum und Geschoss.'}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Elektro-Produktdaten konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!projekt || !gruppen) {
    return (
      <>
        {kopf}
        <LoadingState text="Bauteilarten werden geladen …" zeilen={8} spalten={5} />
      </>
    );
  }

  const bauteile = gruppen.reduce((s, g) => s + g.bauteile, 0);
  const mitFabrikat = gruppen.reduce((s, g) => s + g.mitFabrikat, 0);

  return (
    <>
      {kopf}

      <KpiReihe>
        <KpiCard titel="Bauteile" wert={bauteile} />
        <KpiCard titel="Bauteilarten" wert={gruppen.length} />
        <KpiCard
          titel="mit Fabrikat" wert={mitFabrikat}
          vergleich={`von ${bauteile.toLocaleString('de-DE')} Bauteilen`}
          zustand={mitFabrikat < bauteile ? 'warnung' : 'gut'}
          zustandText={mitFabrikat < bauteile
            ? `${(bauteile - mitFabrikat).toLocaleString('de-DE')} ohne Fabrikat`
            : 'vollständig'}
        />
      </KpiReihe>

      <DataTable
        name="Bauteilarten"
        spalten={spalten}
        zeilen={gruppen}
        schluessel={(g) => g.bauteilart}
        ausfuhr
        ausfuhrDateiname="elektro-bauteilarten"
        beiZeileKlick={(g) => setGewaehlt(gewaehlt === g.bauteilart ? null : g.bauteilart)}
        aktiveZeile={gewaehlt ?? undefined}
        leer={(
          <EmptyState
            titel="Für dieses Modell ist noch kein Elektrobauteil aufgenommen."
            erklaerung="Die Bauteilarten entstehen aus der Modellaufnahme des Gewerks Elektro."
          />
        )}
      />

      {gewaehlt && (
        <Bauteilliste api={api} projektId={projekt.id} bauteilart={gewaehlt}
                      bezeichnung={gruppen.find(
                        (g) => g.bauteilart === gewaehlt)?.bezeichnung
                        ?? gewaehlt} />
      )}
    </>
  );
}

function Bauteilliste({ api, projektId, bauteilart, bezeichnung }: {
  api: ApiClient;
  projektId: string;
  bauteilart: string;
  bezeichnung: string;
}) {
  const [daten, setDaten] = useState<ElektroBauteilseite | null>(null);
  const [seite, setSeite] = useState(0);
  const [raum, setRaum] = useState('');
  const [geschoss, setGeschoss] = useState('');
  const [suche, setSuche] = useState('');
  const [laedt, setLaedt] = useState(false);

  // Der Filter setzt die Seite zurück. Ohne das stünde man nach dem Filtern
  // auf Seite 40 einer Liste mit drei Treffern — und sähe nichts.
  useEffect(() => { setSeite(0); }, [raum, geschoss, suche, bauteilart]);

  useEffect(() => {
    setLaedt(true);

    const zeit = setTimeout(() => {
      api.elektroProduktbauteile(projektId, {
        bauteilart,
        raum: raum || undefined,
        geschoss: geschoss || undefined,
        suche: suche || undefined,
        seite,
      })
        .then(setDaten)
        .finally(() => setLaedt(false));
    }, 250);   // Nicht bei jedem Tastendruck fragen.

    return () => clearTimeout(zeit);
  }, [api, projektId, bauteilart, raum, geschoss, suche, seite]);

  const spalten: Spalte<any>[] = useMemo(() => [
    {
      schluessel: 'fabrikatstand', titel: 'Fabrikat', art: 'status', breite: '150px',
      wert: (b) => (b.hersteller ? 'vorhanden' : 'fehlt'),
      inhalt: (b) => (b.hersteller
        ? <StatusBadge zustand="gut" text="Fabrikat" klein />
        : <StatusBadge zustand="warnung" text="kein Fabrikat" klein />),
    },
    {
      schluessel: 'kennzeichen', titel: 'Kennzeichen', immerSichtbar: true,
      wert: (b) => b.kennzeichen ?? b.bezeichnung ?? b.id,
      inhalt: (b) => (
        <Link to={`../elektro-geraete/${b.id}`}>
          {b.kennzeichen ?? b.bezeichnung ?? b.id.slice(0, 8)}
        </Link>
      ),
    },
    { schluessel: 'typ', titel: 'Typ', wert: (b) => b.typ },
    {
      schluessel: 'produkt', titel: 'Hersteller · Modell',
      wert: (b) => [b.hersteller, b.modell].filter(Boolean).join(' · '),
      inhalt: (b) => (b.hersteller
        ? <>{b.hersteller}{b.modell ? ` · ${b.modell}` : ''}</>
        : <em className="schwach">kein Fabrikat</em>),
    },
    {
      schluessel: 'raum', titel: 'Raum',
      wert: (b) => [b.raumnummer, b.raumname].filter(Boolean).join(' · '),
      inhalt: (b) => (
        <>
          {b.raumnummer ?? '—'}
          {b.raumname && <span className="schwach"> · {b.raumname}</span>}
        </>
      ),
    },
    {
      schluessel: 'geschoss', titel: 'Geschoss',
      wert: (b) => b.geschossgruppe ?? b.geschoss,
      inhalt: (b) => <>{b.geschossgruppe ?? b.geschoss ?? '—'}</>,
    },
    { schluessel: 'werte', titel: 'Werte', art: 'zahl', wert: (b) => b.werte },
  ], []);

  const gefiltert = raum !== '' || geschoss !== '' || suche !== '';

  return (
    <ContentSection
      titel={bezeichnung}
      meta={daten ? `${daten.gesamt.toLocaleString('de-DE')} Bauteile` : undefined}
      werkzeuge={(
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Feld
            beschriftung="Raumnummer" wert={raum} breite="drittel"
            beiAenderung={(w) => setRaum(w as string)}
          />
          <Feld
            beschriftung="Geschoss" wert={geschoss} breite="drittel"
            beiAenderung={(w) => setGeschoss(w as string)}
          />
          <Feld
            beschriftung="Suche" art="suche" wert={suche} breite="drittel"
            beiAenderung={(w) => setSuche(w as string)}
            hinweis="Kennzeichen, Typ oder Raumname"
          />
        </div>
      )}
      nackt
    >
      <DataTable
        name={bezeichnung}
        spalten={spalten}
        zeilen={daten?.bauteile ?? []}
        schluessel={(b) => String(b.id)}
        laedt={laedt && !daten}
        suche={false}
        proSeite={0}
        trefferText={daten
          ? `${daten.gesamt.toLocaleString('de-DE')} Bauteile · Seite ${seite + 1} von ${daten.seiten}`
          : undefined}
        ausfuhr
        ausfuhrDateiname={`elektro-${bauteilart}`}
        leer={(
          <EmptyState
            titel={gefiltert
              ? 'Kein Bauteil trifft diese Filter.'
              : `Für „${bezeichnung}“ ist kein Bauteil aufgenommen.`}
            erklaerung={gefiltert
              ? 'Raum, Geschoss und Suche wirken auf dem Server und schränken die Menge ein.'
              : 'Die Bauteile entstehen aus der Modellaufnahme des Gewerks Elektro.'}
            aktion={gefiltert ? (
              <button
                className="m-knopf"
                onClick={() => { setRaum(''); setGeschoss(''); setSuche(''); }}
              >
                Filter aufheben
              </button>
            ) : undefined}
          />
        )}
      />

      {daten && daten.seiten > 1 && (
        <nav
          aria-label="Blättern"
          style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}
        >
          <button className="m-knopf" onClick={() => setSeite(0)} disabled={seite === 0}
                  aria-label="Erste Seite">⇤</button>
          <button className="m-knopf" onClick={() => setSeite(Math.max(0, seite - 1))}
                  disabled={seite === 0} aria-label="Vorige Seite">←</button>

          <span className="schwach" style={{ fontSize: 13 }}>
            Seite {seite + 1} von {daten.seiten}
            {' · '}
            {(seite * daten.proSeite + 1).toLocaleString('de-DE')}
            {' bis '}
            {Math.min((seite + 1) * daten.proSeite, daten.gesamt).toLocaleString('de-DE')}
            {' von '}
            {daten.gesamt.toLocaleString('de-DE')}
          </span>

          <button className="m-knopf"
                  onClick={() => setSeite(Math.min(daten.seiten - 1, seite + 1))}
                  disabled={seite >= daten.seiten - 1}
                  aria-label="Nächste Seite">→</button>
          <button className="m-knopf" onClick={() => setSeite(daten.seiten - 1)}
                  disabled={seite >= daten.seiten - 1}
                  aria-label="Letzte Seite">⇥</button>
        </nav>
      )}
    </ContentSection>
  );
}
